// MapYandexClustered.tsx
import React, { useEffect, useRef } from 'react'
import { YMaps, Map, Placemark, Clusterer, useYMaps } from '@pbe/react-yandex-maps'
import { lang } from '@src/lang'

export interface MarkerData {
    lat: number
    lng: number
    info: {
        class_name: string
        area_px: number
        volume_m3: number
        mass_kg: number
    }
    icon?: unknown
}

interface Props {
    markers: MarkerData[]
}

/** Палитра как в исходном коде */
const classColorsByName: Record<string, string> = {
    железо: '#FFFFFF',
    'рыболовные снасти': '#00FFFF',
    пластик: '#00FF00',
    дерево: '#FF0000',
    бетон: '#FF00FF',
    резина: '#FFFF00',
}
const resolveColorByName = (name: string) =>
    classColorsByName[name.trim().toLowerCase()] ?? '#808080'

const makeBalloonHtml = (m: MarkerData) => `
  <div style="font-size:14px;line-height:1.4">
    <b>${m.info.class_name}</b><br/>
    ${lang.area}: ${m.info.area_px} px<br/>
    ${lang.volume}: ${m.info.volume_m3.toFixed(3)} m³<br/>
    ${lang.mass}: ${m.info.mass_kg.toFixed(2)} kg
  </div>
`

/** Хелпер для авто-fit границ по маркерам */
const FitToMarkers: React.FC<{
    mapRef: React.RefObject<any | null>
    markers: MarkerData[]
    padding?: number | number[]
}> = ({ mapRef, markers, padding = 40 }) => {
    const ymaps = useYMaps()

    useEffect(() => {
        if (!ymaps || !mapRef.current || !markers?.length) return
        const points = markers.map(m => [m.lat, m.lng])
        const bounds = ymaps.util.bounds.fromPoints(points as any)
        if (bounds) {
            mapRef.current.setBounds(bounds, { checkZoomRange: true, zoomMargin: padding })
        }
    }, [ymaps, mapRef, markers, padding])

    return null
}

export const YMapComponent: React.FC<Props> = ({ markers }) => {
    const mapRef = useRef<any | null>(null)

    return (
        <YMaps query={{ lang: 'ru_RU', coordorder: 'latlong' }} preload={true}>
            <Map
                defaultState={{ center: [0, 0], zoom: 2 }}
                width="100%"
                height="100%"
                instanceRef={ref => {
                    mapRef.current = ref
                }}
            >
                <FitToMarkers mapRef={mapRef} markers={markers} />

                <Clusterer
                    options={{
                        gridSize: 64,
                        maxZoom: 16,
                        hasBalloon: true,
                        clusterDisableClickZoom: false,
                        clusterOpenBalloonOnClick: true, // балун кластера по клику
                        preset: 'islands#invertedBlueClusterIcons',
                        // По желанию — балун кластера-карусель
                        // clusterBalloonContentLayout: 'cluster#balloonCarousel',
                        // clusterBalloonPanelMaxMapArea: 0,
                    }}
                >
                    {markers.map((marker, idx) => (
                        <Placemark
                            key={idx}
                            geometry={[marker.lat, marker.lng]}
                            /** НУЖНО: подключаем модули для балуна/подсказки */
                            modules={['geoObject.addon.balloon', 'geoObject.addon.hint']}
                            options={{
                                preset: 'islands#circleIcon',
                                iconColor: resolveColorByName(marker.info.class_name),
                                hasBalloon: true,
                                openBalloonOnClick: true, // открыть балун по клику
                            }}
                            properties={{
                                balloonContent: makeBalloonHtml(marker),
                                hintContent: marker.info.class_name,
                            }}
                            /** На всякий случай — явное открытие балуна (полезно внутри кластеров) */
                            onClick={(e: any) => {
                                const placemark = e.get('target')
                                if (placemark?.balloon) {
                                    placemark.balloon.open()
                                }
                            }}
                        />
                    ))}
                </Clusterer>
            </Map>
        </YMaps>
    )
}
